import { useEffect, useState, useMemo, type CSSProperties, type FormEvent } from "react";
import {
  listRepInteractions,
  saveRepInteraction,
  listAllocationRequests,
  saveAllocationRequest,
  type RepInteraction,
  type AllocationRequest,
} from "../../lib/api";
import WhiskeyStudioTabs from "../../components/admin/WhiskeyStudioTabs";

export default function AdminWhiskeyIntel() {
  const [activeTab, setActiveTab] = useState<"interactions" | "allocations">("interactions");
  const [loading, setLoading] = useState(true);

  // Rep Interactions
  const [interactions, setInteractions] = useState<RepInteraction[]>([]);
  const [repSearch, setRepSearch] = useState("");
  const [isRepModalOpen, setIsRepModalOpen] = useState(false);
  const [editingRep, setEditingRep] = useState<Partial<RepInteraction> | null>(null);
  const [savingRep, setSavingRep] = useState(false);

  // Allocation Requests
  const [allocations, setAllocations] = useState<AllocationRequest[]>([]);
  const [allocSearch, setAllocSearch] = useState("");
  const [allocStatusFilter, setAllocStatusFilter] = useState("all");
  const [isAllocModalOpen, setIsAllocModalOpen] = useState(false);
  const [editingAlloc, setEditingAlloc] = useState<Partial<AllocationRequest> | null>(null);
  const [savingAlloc, setSavingAlloc] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isRepModalOpen) setIsRepModalOpen(false);
        if (isAllocModalOpen) setIsAllocModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isRepModalOpen, isAllocModalOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reps, allocs] = await Promise.all([
        listRepInteractions(),
        listAllocationRequests(),
      ]);
      setInteractions(reps || []);
      setAllocations(allocs || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load intelligence data", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredInteractions = useMemo(() => {
    if (!repSearch.trim()) return interactions;
    const q = repSearch.toLowerCase().trim();
    return interactions.filter(
      (r) =>
        r.rep_name.toLowerCase().includes(q) ||
        r.distributor.toLowerCase().includes(q) ||
        (r.notes && r.notes.toLowerCase().includes(q))
    );
  }, [interactions, repSearch]);

  const filteredAllocations = useMemo(() => {
    let list = allocations;
    if (allocStatusFilter !== "all") {
      list = list.filter((a) => (a.status || "").toLowerCase() === allocStatusFilter);
    }
    if (allocSearch.trim()) {
      const q = allocSearch.toLowerCase().trim();
      list = list.filter(
        (a) =>
          a.bottle_name.toLowerCase().includes(q) ||
          a.distributor.toLowerCase().includes(q) ||
          (a.notes && a.notes.toLowerCase().includes(q))
      );
    }
    return list;
  }, [allocations, allocStatusFilter, allocSearch]);

  // KPIs
  const grantedCount = allocations.filter((a) => a.status === "granted" || a.status === "partial").length;
  const hitRate = allocations.length > 0 ? Math.round((grantedCount / allocations.length) * 100) : 0;
  const pendingFollowups = interactions.filter((i) => i.follow_up_needed).length;

  const handleNewRep = () => {
    setEditingRep({
      rep_name: "",
      distributor: "Southern Glazer's",
      contact_type: "in_person",
      notes: "",
      interaction_date: new Date().toISOString().split("T")[0],
      follow_up_date: null,
      follow_up_needed: false,
    });
    setIsRepModalOpen(true);
  };

  const handleSaveRep = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingRep || !editingRep.rep_name) return;
    setSavingRep(true);
    try {
      await saveRepInteraction(editingRep);
      showToast("✓ Rep interaction logged");
      setIsRepModalOpen(false);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to save interaction", "err");
    } finally {
      setSavingRep(false);
    }
  };

  const handleNewAlloc = () => {
    setEditingAlloc({
      bottle_name: "",
      distributor: "Southern Glazer's",
      cases_requested: 1,
      cases_granted: null,
      status: "pending",
      requested_at: new Date().toISOString().split("T")[0],
      notes: "",
    });
    setIsAllocModalOpen(true);
  };

  const handleSaveAlloc = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingAlloc || !editingAlloc.bottle_name) return;
    setSavingAlloc(true);
    try {
      await saveAllocationRequest(editingAlloc);
      showToast("✓ Allocation request saved");
      setIsAllocModalOpen(false);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to save allocation", "err");
    } finally {
      setSavingAlloc(false);
    }
  };

  return (
    <div style={styles.container}>
      <WhiskeyStudioTabs activeTab="intel" badgeCounts={{ intel: interactions.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Whiskey Intelligence &amp; Allocations</h1>
          <p style={styles.subtitle}>
            Track distributor rep relationships, rare barrel allocations, hit rates, and follow-ups.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {activeTab === "interactions" ? (
            <button type="button" style={styles.btnPrimary} onClick={handleNewRep}>
              + Log Rep Touchpoint
            </button>
          ) : (
            <button type="button" style={styles.btnPrimary} onClick={handleNewAlloc}>
              + Request Allocation
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div style={styles.kpiGrid}>
        <div style={styles.kpiCard}>
          <div style={styles.kpiValue}>{interactions.length}</div>
          <div style={styles.kpiLabel}>Total Rep Touchpoints</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiValue, color: "var(--gold)" }}>
            {allocations.filter((a) => a.status === "pending").length}
          </div>
          <div style={styles.kpiLabel}>Pending Allocations</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiValue, color: "#27ae60" }}>{hitRate}%</div>
          <div style={styles.kpiLabel}>Allocation Hit Rate</div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiValue, color: pendingFollowups > 0 ? "#E05040" : "var(--muted)" }}>
            {pendingFollowups}
          </div>
          <div style={styles.kpiLabel}>Follow-ups Pending</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabsRow}>
        <button
          type="button"
          style={activeTab === "interactions" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("interactions")}
        >
          Rep Touchpoints ({interactions.length})
        </button>
        <button
          type="button"
          style={activeTab === "allocations" ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab("allocations")}
        >
          Allocation Requests ({allocations.length})
        </button>
      </div>

      {/* ── Tab 1: Rep Touchpoints ──────────────────────────────────────────── */}
      {activeTab === "interactions" && (
        <div>
          <div style={styles.toolbar}>
            <input
              type="text"
              placeholder="Search by rep name, distributor, notes…"
              value={repSearch}
              onChange={(e) => setRepSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {loading ? (
            <div style={styles.loader}>Loading rep interactions…</div>
          ) : filteredInteractions.length === 0 ? (
            <div style={styles.emptyState}>No rep interactions recorded yet.</div>
          ) : (
            <div style={styles.cardsList}>
              {filteredInteractions.map((r) => (
                <div
                  key={r.id}
                  style={styles.repCard}
                  onClick={() => {
                    setEditingRep({ ...r });
                    setIsRepModalOpen(true);
                  }}
                >
                  <div style={styles.cardHeader}>
                    <div>
                      <h3 style={styles.cardName}>{r.rep_name}</h3>
                      <div style={styles.distributorName}>
                        {r.distributor} · <span style={{ textTransform: "capitalize" }}>{r.contact_type.replace("_", " ")}</span>
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: "var(--muted)" }}>
                      {new Date(r.interaction_date).toLocaleDateString()}
                    </span>
                  </div>

                  {r.notes && <p style={styles.notesBody}>{r.notes}</p>}

                  {r.follow_up_needed && (
                    <div style={styles.followupPill}>
                      ⚠ Follow-up scheduled for: {r.follow_up_date || "Soon"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab 2: Allocations ──────────────────────────────────────────────── */}
      {activeTab === "allocations" && (
        <div>
          <div style={styles.toolbar}>
            <input
              type="text"
              placeholder="Search by bottle name, distributor…"
              value={allocSearch}
              onChange={(e) => setAllocSearch(e.target.value)}
              style={styles.searchInput}
            />
            <select
              value={allocStatusFilter}
              onChange={(e) => setAllocStatusFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="granted">Granted</option>
              <option value="partial">Partial</option>
              <option value="denied">Denied</option>
              <option value="ghosted">Ghosted</option>
            </select>
          </div>

          {loading ? (
            <div style={styles.loader}>Loading allocation requests…</div>
          ) : filteredAllocations.length === 0 ? (
            <div style={styles.emptyState}>No allocation requests recorded.</div>
          ) : (
            <div style={styles.cardsList}>
              {filteredAllocations.map((a) => {
                const isGranted = a.status === "granted" || a.status === "partial";
                const isDenied = a.status === "denied";
                return (
                  <div
                    key={a.id}
                    style={styles.allocCard}
                    onClick={() => {
                      setEditingAlloc({ ...a });
                      setIsAllocModalOpen(true);
                    }}
                  >
                    <div style={styles.cardHeader}>
                      <div>
                        <h3 style={styles.cardName}>{a.bottle_name}</h3>
                        <div style={styles.distributorName}>{a.distributor}</div>
                      </div>
                      <span
                        style={{
                          ...styles.statusBadge,
                          background: isGranted
                            ? "rgba(39,174,96,0.15)"
                            : isDenied
                            ? "rgba(224,80,64,0.15)"
                            : "rgba(249,175,54,0.15)",
                          color: isGranted ? "#27ae60" : isDenied ? "#E05040" : "var(--gold)",
                        }}
                      >
                        {(a.status || "draft").toUpperCase()}
                      </span>
                    </div>

                    <div style={styles.allocCounts}>
                      <span>Requested: <strong>{a.cases_requested} case{a.cases_requested > 1 ? "s" : ""}</strong></span>
                      {a.cases_granted != null && (
                        <span>Granted: <strong style={{ color: "#27ae60" }}>{a.cases_granted} case{a.cases_granted > 1 ? "s" : ""}</strong></span>
                      )}
                    </div>

                    {a.notes && <p style={styles.notesBody}>{a.notes}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Rep Interaction Modal */}
      {isRepModalOpen && editingRep && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingRep.id ? "Edit Rep Touchpoint" : "Log Rep Touchpoint"}
              </h2>
              <button
                type="button"
                style={styles.btnIcon}
                onClick={() => setIsRepModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveRep} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Rep Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Mike Ross"
                    value={editingRep.rep_name || ""}
                    onChange={(e) =>
                      setEditingRep({ ...editingRep, rep_name: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Distributor / Brand *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Southern Glazer's"
                    value={editingRep.distributor || ""}
                    onChange={(e) =>
                      setEditingRep({ ...editingRep, distributor: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Contact Channel</label>
                  <select
                    value={editingRep.contact_type || "in_person"}
                    onChange={(e) =>
                      setEditingRep({ ...editingRep, contact_type: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="in_person">In Person (At Bar)</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone Call</option>
                    <option value="text">SMS / Text</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Touchpoint Date</label>
                  <input
                    type="date"
                    value={editingRep.interaction_date || ""}
                    onChange={(e) =>
                      setEditingRep({ ...editingRep, interaction_date: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Meeting &amp; Tasting Notes</label>
                  <textarea
                    rows={3}
                    placeholder="Sampled new rye barrels, discussed Q4 bourbon allocations..."
                    value={editingRep.notes || ""}
                    onChange={(e) =>
                      setEditingRep({ ...editingRep, notes: e.target.value })
                    }
                    style={styles.textarea}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text)" }}>
                    <input
                      type="checkbox"
                      checked={editingRep.follow_up_needed || false}
                      onChange={(e) =>
                        setEditingRep({ ...editingRep, follow_up_needed: e.target.checked })
                      }
                    />
                    Follow-up action required
                  </label>
                </div>

                {editingRep.follow_up_needed && (
                  <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                    <label style={styles.label}>Follow-up Target Date</label>
                    <input
                      type="date"
                      value={editingRep.follow_up_date || ""}
                      onChange={(e) =>
                        setEditingRep({ ...editingRep, follow_up_date: e.target.value })
                      }
                      style={styles.input}
                    />
                  </div>
                )}
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.btnSecondary}
                  onClick={() => setIsRepModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.btnPrimary} disabled={savingRep}>
                  {savingRep ? "Saving…" : "Save Touchpoint"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Allocation Modal */}
      {isAllocModalOpen && editingAlloc && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingAlloc.id ? "Edit Allocation Request" : "New Allocation Request"}
              </h2>
              <button
                type="button"
                style={styles.btnIcon}
                onClick={() => setIsAllocModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveAlloc} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Rare Bottle Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Pappy Van Winkle 15yr"
                    value={editingAlloc.bottle_name || ""}
                    onChange={(e) =>
                      setEditingAlloc({ ...editingAlloc, bottle_name: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Distributor *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Southern Glazer's"
                    value={editingAlloc.distributor || ""}
                    onChange={(e) =>
                      setEditingAlloc({ ...editingAlloc, distributor: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Allocation Status</label>
                  <select
                    value={editingAlloc.status || "pending"}
                    onChange={(e) =>
                      setEditingAlloc({ ...editingAlloc, status: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="pending">Pending Review</option>
                    <option value="granted">Granted (Full)</option>
                    <option value="partial">Granted (Partial)</option>
                    <option value="denied">Denied</option>
                    <option value="ghosted">Ghosted / No Reply</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Cases Requested</label>
                  <input
                    type="number"
                    min="1"
                    value={editingAlloc.cases_requested || 1}
                    onChange={(e) =>
                      setEditingAlloc({
                        ...editingAlloc,
                        cases_requested: parseInt(e.target.value, 10) || 1,
                      })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Cases Granted</label>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={editingAlloc.cases_granted ?? ""}
                    onChange={(e) =>
                      setEditingAlloc({
                        ...editingAlloc,
                        cases_granted: e.target.value ? parseInt(e.target.value, 10) : null,
                      })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Notes &amp; Pricing</label>
                  <textarea
                    rows={3}
                    placeholder="Quoted $180/case, promised delivery by October..."
                    value={editingAlloc.notes || ""}
                    onChange={(e) =>
                      setEditingAlloc({ ...editingAlloc, notes: e.target.value })
                    }
                    style={styles.textarea}
                  />
                </div>
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.btnSecondary}
                  onClick={() => setIsAllocModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.btnPrimary} disabled={savingAlloc}>
                  {savingAlloc ? "Saving…" : "Save Allocation"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 },
  kpiCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 14, textAlign: "center" },
  kpiValue: { fontSize: 22, fontWeight: 800, color: "var(--text)" },
  kpiLabel: { fontSize: 11, color: "var(--muted)", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.4 },
  tabsRow: { display: "flex", gap: 8, borderBottom: "1px solid var(--border)", paddingBottom: 8, marginBottom: 20 },
  tab: { background: "transparent", border: 0, color: "var(--muted)", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 },
  tabActive: { background: "rgba(249,175,54,0.15)", border: 0, color: "var(--gold)", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13 },
  toolbar: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 },
  searchInput: { flex: 1, minWidth: 240, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  select: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  cardsList: { display: "grid", gap: 10 },
  repCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 16, cursor: "pointer" },
  allocCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 16, cursor: "pointer" },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 },
  cardName: { fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" },
  distributorName: { fontSize: 12, color: "var(--gold)", marginTop: 2 },
  notesBody: { fontSize: 13, color: "var(--muted)", margin: "8px 0 0", lineHeight: 1.4 },
  followupPill: { marginTop: 8, fontSize: 11, fontWeight: 700, color: "#E05040", background: "rgba(224,80,64,0.1)", padding: "4px 8px", borderRadius: 4, display: "inline-block" },
  statusBadge: { fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4 },
  allocCounts: { display: "flex", gap: 16, fontSize: 12, color: "var(--muted)", marginTop: 4 },
  btnPrimary: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnIcon: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 22, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 600, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  modalForm: { padding: 20, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5 },
  input: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  textarea: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13, resize: "vertical" },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid var(--border)", paddingTop: 16 },
};
